/**
 * Tile catalog for the customizable Marketing Dashboard overview.
 *
 * Every metric the overview can show is declared once here with its label,
 * how to render its value, and how to derive its period-over-period delta.
 * The Overview tab renders the subset the operator pinned (persisted via
 * `preferences.ts`); the tile *picker* renders this whole catalog.
 *
 * Two rules keep the catalog honest:
 *
 *  1. A tile is `available: false` when its provider has no data for the
 *     window. Pinned-but-unavailable tiles still render, showing an
 *     "awaiting sync" state rather than a confident zero — a zero on a
 *     revenue tile in a report the team reviews every two weeks is a
 *     materially misleading thing to display.
 *
 *  2. Metrics where lower is better (average search position, CPC, CPA,
 *     TACoS) declare `lowerIsBetter`, and their delta is sign-flipped by
 *     `pctChangeInverted` so the shared green-up / red-down MetricCard
 *     treatment reads correctly without per-tile special-casing.
 */

import {
  Activity,
  ArrowUpRight,
  BadgeDollarSign,
  Boxes,
  Clock3,
  Coins,
  Eye,
  Film,
  Heart,
  type LucideIcon,
  MousePointerClick,
  Package,
  PiggyBank,
  Search,
  ShoppingCart,
  Target,
  TrendingUp,
  UserPlus,
  Users,
} from 'lucide-react';
import { formatCurrency } from '@/lib/formatters';
import {
  formatCompactNumber,
  formatDuration,
  formatRatioPercent,
  pctChange,
  pctChangeInverted,
} from './range';
import type {
  AdsAnalyticsData,
  AmazonAnalyticsData,
  SearchConsoleData,
  SocialAnalyticsData,
  WebsiteTotalsData,
} from './hooks';

export type MarketingTileGroup = 'Website' | 'Search' | 'Social' | 'Paid ads' | 'Amazon';

export const MARKETING_TILE_GROUPS: MarketingTileGroup[] = [
  'Website',
  'Search',
  'Social',
  'Paid ads',
  'Amazon',
];

export interface MarketingTile {
  id: string;
  label: string;
  group: MarketingTileGroup;
  icon: LucideIcon;
  /** Formatted headline value, e.g. "12.4K" or "$8,120.00". */
  value: string;
  /** Period-over-period change as a 0..1 ratio, or null when incomparable. */
  delta: number | null;
  /** Secondary line under the value. */
  hint?: string;
  /** False when the provider reported nothing for this window. */
  available: boolean;
  /** Which integration the operator needs to connect, when unavailable. */
  requires: string;
}

/** Inputs the catalog derives every tile from. */
export interface MarketingTileSources {
  website: WebsiteTotalsData | undefined;
  search: SearchConsoleData | undefined;
  social: SocialAnalyticsData | undefined;
  ads: AdsAnalyticsData | undefined;
  amazon: AmazonAnalyticsData | undefined;
}

/**
 * The tiles shown to an operator who has never customized the dashboard.
 *
 * Chosen to answer the five questions a bi-weekly marketing review actually
 * opens with: is traffic growing, is organic search growing, is the audience
 * growing, is paid spend efficient, and is the storefront converting.
 */
export const DEFAULT_MARKETING_TILE_IDS = [
  'web_users',
  'web_conversions',
  'gsc_clicks',
  'gsc_position',
  'social_followers',
  'social_engagements',
  'ads_spend',
  'ads_roas',
  'amazon_revenue',
  'amazon_orders',
] as const;

/**
 * Builds the full catalog for the current window.
 *
 * Returned in a stable order (grouped by channel) so the tile picker doesn't
 * reshuffle between renders; the Overview tab reorders to match the user's
 * pinned sequence.
 */
export function buildMarketingTiles(sources: MarketingTileSources): MarketingTile[] {
  return [
    ...websiteTiles(sources.website),
    ...searchTiles(sources.search),
    ...socialTiles(sources.social),
    ...adsTiles(sources.ads),
    ...amazonTiles(sources.amazon),
  ];
}

/** Index for O(1) lookup when rendering a pinned subset. */
export function indexTiles(tiles: MarketingTile[]): Map<string, MarketingTile> {
  return new Map(tiles.map((t) => [t.id, t]));
}

// ─── Website ───────────────────────────────────────────────────────────

function websiteTiles(data: WebsiteTotalsData | undefined): MarketingTile[] {
  const totals = data?.totals;
  const previous = data?.previous;
  const available = !!data?.connected;
  const base = { group: 'Website' as const, available, requires: 'Google Analytics 4' };

  return [
    {
      ...base,
      id: 'web_users',
      label: 'Website users',
      icon: Users,
      value: formatCompactNumber(totals?.users ?? 0),
      delta: pctChange(totals?.users ?? 0, previous?.users ?? 0),
      hint: totals
        ? `${formatRatioPercent(totals.newUserShare)} new visitors`
        : undefined,
    },
    {
      ...base,
      id: 'web_sessions',
      label: 'Sessions',
      icon: Activity,
      value: formatCompactNumber(totals?.sessions ?? 0),
      delta: pctChange(totals?.sessions ?? 0, previous?.sessions ?? 0),
    },
    {
      ...base,
      id: 'web_new_users',
      label: 'New visitors',
      icon: UserPlus,
      value: formatCompactNumber(totals?.newUsers ?? 0),
      delta: pctChange(totals?.newUsers ?? 0, previous?.newUsers ?? 0),
    },
    {
      ...base,
      id: 'web_engagement_rate',
      label: 'Engagement rate',
      icon: MousePointerClick,
      value: formatRatioPercent(totals?.engagementRate ?? 0),
      delta: pctChange(totals?.engagementRate ?? 0, previous?.engagementRate ?? 0),
      hint: totals
        ? `${formatDuration(totals.avgEngagementSeconds)} avg per session`
        : undefined,
    },
    {
      ...base,
      id: 'web_avg_engagement',
      label: 'Avg engagement time',
      icon: Clock3,
      value: formatDuration(totals?.avgEngagementSeconds ?? 0),
      delta: pctChange(
        totals?.avgEngagementSeconds ?? 0,
        previous?.avgEngagementSeconds ?? 0,
      ),
    },
    {
      ...base,
      id: 'web_conversions',
      label: 'Website conversions',
      icon: ArrowUpRight,
      value: formatCompactNumber(totals?.conversions ?? 0),
      delta: pctChange(totals?.conversions ?? 0, previous?.conversions ?? 0),
      hint: totals
        ? `${formatRatioPercent(totals.conversionRate, 2)} of sessions`
        : undefined,
    },
    {
      ...base,
      id: 'web_conversion_rate',
      label: 'Conversion rate',
      icon: Target,
      value: formatRatioPercent(totals?.conversionRate ?? 0, 2),
      delta: pctChange(totals?.conversionRate ?? 0, previous?.conversionRate ?? 0),
    },
  ];
}

// ─── Search ────────────────────────────────────────────────────────────

function searchTiles(data: SearchConsoleData | undefined): MarketingTile[] {
  const totals = data?.totals;
  const previous = data?.previous;
  const available = !!data?.connected;
  const base = { group: 'Search' as const, available, requires: 'Google Search Console' };

  return [
    {
      ...base,
      id: 'gsc_clicks',
      label: 'Search clicks',
      icon: Search,
      value: formatCompactNumber(totals?.clicks ?? 0),
      delta: pctChange(totals?.clicks ?? 0, previous?.clicks ?? 0),
      hint: totals ? `${formatRatioPercent(totals.ctr, 2)} click-through` : undefined,
    },
    {
      ...base,
      id: 'gsc_impressions',
      label: 'Search impressions',
      icon: Eye,
      value: formatCompactNumber(totals?.impressions ?? 0),
      delta: pctChange(totals?.impressions ?? 0, previous?.impressions ?? 0),
    },
    {
      ...base,
      id: 'gsc_ctr',
      label: 'Search CTR',
      icon: MousePointerClick,
      value: formatRatioPercent(totals?.ctr ?? 0, 2),
      delta: pctChange(totals?.ctr ?? 0, previous?.ctr ?? 0),
    },
    {
      ...base,
      id: 'gsc_position',
      label: 'Avg search position',
      icon: TrendingUp,
      // Position 1 is the top of page 1, so a *decrease* is an improvement —
      // hence the inverted delta.
      value: totals?.position ? totals.position.toFixed(1) : '—',
      delta: pctChangeInverted(totals?.position ?? 0, previous?.position ?? 0),
    },
  ];
}

// ─── Social ────────────────────────────────────────────────────────────

function socialTiles(data: SocialAnalyticsData | undefined): MarketingTile[] {
  const totals = data?.totals;
  const previous = data?.previous;
  const available = !!data?.connected;
  const base = {
    group: 'Social' as const,
    available,
    requires: 'Facebook, Instagram, TikTok, or YouTube',
  };

  return [
    {
      ...base,
      id: 'social_followers',
      label: 'Total followers',
      icon: Users,
      value: formatCompactNumber(totals?.followers ?? 0),
      // Followers is a snapshot, so the meaningful comparison is the growth
      // that happened during the window, not last period's headcount.
      delta: pctChange(totals?.followerGrowth ?? 0, previous?.followerGrowth ?? 0),
      hint: totals
        ? `${totals.followerGrowth >= 0 ? '+' : ''}${totals.followerGrowth.toLocaleString()} this period`
        : undefined,
    },
    {
      ...base,
      id: 'social_follower_growth',
      label: 'Follower growth',
      icon: UserPlus,
      value: `${(totals?.followerGrowth ?? 0) >= 0 ? '+' : ''}${formatCompactNumber(totals?.followerGrowth ?? 0)}`,
      delta: pctChange(totals?.followerGrowth ?? 0, previous?.followerGrowth ?? 0),
    },
    {
      ...base,
      id: 'social_engagements',
      label: 'Social engagements',
      icon: Heart,
      value: formatCompactNumber(totals?.engagements ?? 0),
      delta: pctChange(totals?.engagements ?? 0, previous?.engagements ?? 0),
      hint: totals ? `${formatRatioPercent(totals.engagementRate, 2)} rate` : undefined,
    },
    {
      ...base,
      id: 'social_reach',
      label: 'Social reach',
      icon: Eye,
      value: formatCompactNumber(totals?.reach ?? 0),
      delta: pctChange(totals?.reach ?? 0, previous?.reach ?? 0),
    },
    {
      ...base,
      id: 'social_video_views',
      label: 'Video views',
      icon: Film,
      value: formatCompactNumber(totals?.videoViews ?? 0),
      delta: pctChange(totals?.videoViews ?? 0, previous?.videoViews ?? 0),
      hint: totals?.watchTimeSeconds
        ? `${formatDuration(totals.watchTimeSeconds)} watched`
        : undefined,
    },
    {
      ...base,
      id: 'social_posts',
      label: 'Posts published',
      icon: Film,
      value: (totals?.postsPublished ?? 0).toLocaleString(),
      delta: pctChange(totals?.postsPublished ?? 0, previous?.postsPublished ?? 0),
    },
  ];
}

// ─── Paid ads ──────────────────────────────────────────────────────────

function adsTiles(data: AdsAnalyticsData | undefined): MarketingTile[] {
  const totals = data?.totals;
  const previous = data?.previous;
  const available = !!data?.connected;
  const base = {
    group: 'Paid ads' as const,
    available,
    requires: 'Google Ads, Meta Ads, Amazon Ads, or TikTok Ads',
  };

  return [
    {
      ...base,
      id: 'ads_spend',
      label: 'Ad spend',
      icon: Coins,
      value: formatCurrency(totals?.spendCents ?? 0, { whole: true }),
      // Spend going up isn't inherently good or bad, so this stays a plain
      // delta — ROAS and CPA are the tiles that judge efficiency.
      delta: pctChange(totals?.spendCents ?? 0, previous?.spendCents ?? 0),
    },
    {
      ...base,
      id: 'ads_roas',
      label: 'ROAS',
      icon: PiggyBank,
      value: totals?.roas ? `${totals.roas.toFixed(2)}×` : '—',
      delta: pctChange(totals?.roas ?? 0, previous?.roas ?? 0),
    },
    {
      ...base,
      id: 'ads_revenue',
      label: 'Ad-attributed revenue',
      icon: BadgeDollarSign,
      value: formatCurrency(totals?.revenueCents ?? 0, { whole: true }),
      delta: pctChange(totals?.revenueCents ?? 0, previous?.revenueCents ?? 0),
    },
    {
      ...base,
      id: 'ads_conversions',
      label: 'Ad conversions',
      icon: Target,
      value: formatCompactNumber(totals?.conversions ?? 0),
      delta: pctChange(totals?.conversions ?? 0, previous?.conversions ?? 0),
    },
    {
      ...base,
      id: 'ads_cpa',
      label: 'Cost per acquisition',
      icon: Target,
      value: totals?.cpaCents ? formatCurrency(totals.cpaCents) : '—',
      delta: pctChangeInverted(totals?.cpaCents ?? 0, previous?.cpaCents ?? 0),
    },
    {
      ...base,
      id: 'ads_cpc',
      label: 'Cost per click',
      icon: MousePointerClick,
      value: totals?.cpcCents ? formatCurrency(totals.cpcCents) : '—',
      delta: pctChangeInverted(totals?.cpcCents ?? 0, previous?.cpcCents ?? 0),
    },
    {
      ...base,
      id: 'ads_ctr',
      label: 'Ad CTR',
      icon: MousePointerClick,
      value: formatRatioPercent(totals?.ctr ?? 0, 2),
      delta: pctChange(totals?.ctr ?? 0, previous?.ctr ?? 0),
    },
  ];
}

// ─── Amazon ────────────────────────────────────────────────────────────

function amazonTiles(data: AmazonAnalyticsData | undefined): MarketingTile[] {
  const totals = data?.totals;
  const previous = data?.previous;
  const available = !!data?.connected;
  const base = { group: 'Amazon' as const, available, requires: 'Amazon Seller Central' };
  const ads = data?.ads;

  return [
    {
      ...base,
      id: 'amazon_revenue',
      label: 'Amazon revenue',
      icon: BadgeDollarSign,
      value: formatCurrency(totals?.revenueCents ?? 0, { whole: true }),
      delta: pctChange(totals?.revenueCents ?? 0, previous?.revenueCents ?? 0),
    },
    {
      ...base,
      id: 'amazon_orders',
      label: 'Amazon orders',
      icon: ShoppingCart,
      value: (totals?.orders ?? 0).toLocaleString(),
      delta: pctChange(totals?.orders ?? 0, previous?.orders ?? 0),
    },
    {
      ...base,
      id: 'amazon_aov',
      label: 'Amazon AOV',
      icon: Package,
      value: totals?.aovCents ? formatCurrency(totals.aovCents) : '—',
      delta: pctChange(totals?.aovCents ?? 0, previous?.aovCents ?? 0),
    },
    {
      ...base,
      id: 'amazon_tacos',
      label: 'TACoS',
      icon: Boxes,
      // Total advertising cost of sale: ad spend ÷ total Amazon revenue.
      // Needs both the seller sync and the ads sync to be meaningful.
      available: available && !!ads?.connected,
      requires: 'Amazon Seller Central + Amazon Ads',
      value: formatRatioPercent(totals?.tacos ?? 0, 2),
      delta: pctChangeInverted(totals?.tacos ?? 0, previous?.tacos ?? 0),
    },
    {
      ...base,
      id: 'amazon_ad_spend',
      label: 'Amazon ad spend',
      icon: Coins,
      available: !!ads?.connected,
      requires: 'Amazon Ads',
      value: formatCurrency(ads?.totals.spendCents ?? 0, { whole: true }),
      delta: pctChange(ads?.totals.spendCents ?? 0, ads?.previous.spendCents ?? 0),
    },
  ];
}
